import type {
  DetailItem,
  HighLevelIssue,
  NavNeighbor,
  ResolvedIssueLink,
  SpecificIssue,
  WebsiteContent,
} from '../types/content';
import { CATEGORY_LABELS } from '../config/appConfig';

export function getItem(data: WebsiteContent, id: string): DetailItem | undefined {
  return data.index.byId[id];
}

export function getItemBySlug(data: WebsiteContent, slug: string): DetailItem | undefined {
  const ids = data.index.bySlug[slug];
  if (!ids?.length) return undefined;
  return getItem(data, ids[0]);
}

export function findHighLevelIssue(
  data: WebsiteContent,
  slugOrId: string,
): HighLevelIssue | undefined {
  return data.highLevelIssues.find((h) => h.slug === slugOrId || h.id === slugOrId);
}

/** Match a specific issue by full id, slug, or short id suffix (e.g. `executive_overreach`). */
export function findSpecificInHighLevel(
  highLevel: HighLevelIssue,
  specificRef: string,
): SpecificIssue | undefined {
  return highLevel.specificIssues.find(
    (s) =>
      s.id === specificRef ||
      s.slug === specificRef ||
      s.id === `${highLevel.id}.${specificRef}` ||
      s.id.endsWith(`.${specificRef}`),
  );
}

export function findSpecificIssue(
  data: WebsiteContent,
  hlSlugOrId: string,
  specificSlugOrId: string,
): { highLevel: HighLevelIssue; specific: SpecificIssue } | undefined {
  const highLevel = findHighLevelIssue(data, hlSlugOrId);
  if (!highLevel) return undefined;
  const specific = findSpecificInHighLevel(highLevel, specificSlugOrId);
  if (!specific) return undefined;
  return { highLevel, specific };
}

/** Resolve an entry's primary voter-issue placement for URLs and breadcrumbs. */
export function resolvePrimaryIssuePath(
  data: WebsiteContent,
  item: DetailItem,
): { highLevel: HighLevelIssue; specific: SpecificIssue } | undefined {
  const highLevel = findHighLevelIssue(data, item.primaryIssueId);
  if (!highLevel) return undefined;
  const specific = findSpecificInHighLevel(highLevel, item.primarySpecificIssueId);
  if (!specific) return undefined;
  return { highLevel, specific };
}

export function getNeighborsInSpecificIssue(
  specific: SpecificIssue,
  itemId: string,
): { prev: NavNeighbor | null; next: NavNeighbor | null } {
  const items = specific.detailItems;
  const idx = items.findIndex((i) => i.id === itemId);
  if (idx === -1) return { prev: null, next: null };

  const total = items.length;
  const toNeighbor = (item: DetailItem, position: number): NavNeighbor => ({
    id: item.id,
    title: item.title,
    slug: item.slug,
    position,
    total,
  });

  return {
    prev: idx > 0 ? toNeighbor(items[idx - 1], idx) : null,
    next: idx < total - 1 ? toNeighbor(items[idx + 1], idx + 2) : null,
  };
}

export function resolveIssueLinks(
  data: WebsiteContent,
  links: DetailItem['issueLinks'],
  excludeSpecificId?: string,
): ResolvedIssueLink[] {
  const seen = new Set<string>();
  const resolved: ResolvedIssueLink[] = [];

  for (const link of links) {
    const key = link.specificIssueId;
    if (seen.has(key) || key === excludeSpecificId) continue;
    seen.add(key);

    const hl = data.highLevelIssues.find((h) => h.id === link.highLevelIssueId);
    const spec = hl ? findSpecificInHighLevel(hl, link.specificIssueId) : undefined;
    if (!hl || !spec) continue;

    resolved.push({
      highLevelIssueId: hl.id,
      highLevelTitle: hl.title,
      highLevelSlug: hl.slug,
      specificIssueId: spec.id,
      specificTitle: spec.title,
      specificSlug: spec.slug,
    });
  }

  return resolved;
}

export function getRelatedItems(
  data: WebsiteContent,
  item: DetailItem,
  limit: number,
): DetailItem[] {
  const scores = new Map<string, number>();

  for (const link of item.issueLinks) {
    const key = link.specificIssueId.includes('.')
      ? link.specificIssueId
      : `${link.highLevelIssueId}.${link.specificIssueId}`;
    const ids = data.index.bySpecificIssue[key] ?? [];
    for (const id of ids) {
      if (id === item.id) continue;
      scores.set(id, (scores.get(id) ?? 0) + 1);
    }
  }

  return [...scores.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([id]) => getItem(data, id))
    .filter((i): i is DetailItem => Boolean(i));
}

export function formatFieldLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category.replace(/_/g, ' ');
}

export function searchItems(data: WebsiteContent, query: string, limit: number): DetailItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const results: { item: DetailItem; score: number }[] = [];

  for (const item of Object.values(data.index.byId)) {
    let score = 0;
    if (item.title.toLowerCase().includes(q)) score += 10;
    if (item.slug.includes(q.replace(/\s+/g, '-'))) score += 6;
    if (item.body.toLowerCase().includes(q)) score += 3;
    const keywordHits = data.index.byKeyword[q];
    if (keywordHits?.includes(item.id)) score += 4;
    if (score > 0) results.push({ item, score });
  }

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((r) => r.item);
}

export function dedupeResources(resources: { label: string | null; url: string }[]) {
  const seen = new Set<string>();
  return resources.filter((r) => {
    const url = r.url.replace(/\]\($/, '').trim();
    if (!url.startsWith('http')) return false;
    if (seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

export function countHighLevelItems(hl: HighLevelIssue): number {
  return hl.uniqueItemCount ?? hl.itemCount ?? hl.specificIssues.reduce((n, s) => n + s.itemCount, 0);
}

/** Canonical URL for a detail item within its primary voter-issue path. */
export function getTopicPath(
  data: WebsiteContent,
  item: DetailItem,
  context?: { highLevelSlug: string; specificSlug: string },
): string {
  if (context) {
    return `/issues/${context.highLevelSlug}/${context.specificSlug}/${item.slug}`;
  }
  const primary = resolvePrimaryIssuePath(data, item);
  if (primary) {
    return `/issues/${primary.highLevel.slug}/${primary.specific.slug}/${item.slug}`;
  }
  return `/topic/${item.slug}`;
}
